import {
  DestroyRef,
  Injectable,
  computed,
  inject,
  signal,
} from '@angular/core';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import {
  filter,
  Observable,
  switchMap,
  of,
  BehaviorSubject,
  catchError,
  shareReplay,
} from 'rxjs';
import { SystemUser, SystemUserAccess } from '../models/systemUser.model';
import {
  AuthClient,
  GetAuthenticatedUserQueryResponse,
  PharmacyAccessResponse,
  UserRole,
} from 'src/httpClient/luminav-link-api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { GridState } from '../shared/directives/track-grid-state/track-grid-state.model';
/**
 * Service responsible for handling Microsoft Authentication Library (MSAL) authentication
 * and managing user authentication state throughout the application.

 * This service provides functionality for:
 * - Managing user authentication state
 * - Handling MSAL account storage events
 * - Checking and managing active accounts
 * - Providing authentication status
 * - Handling user logout
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  /** Subject used for cleanup of subscriptions */
  private readonly destroyRef = inject(DestroyRef);

  /** Subject to trigger user refresh */
  private refreshToken$ = new BehaviorSubject<void>(undefined);

  /** Currently authenticated user account information */
  private _currentUser = signal<SystemUser | null>(null);
  private isRefreshing = signal(false);

  public readonly isLoading = computed(
    () => this._currentUser() == null && this.isRefreshing(),
  );

  public readonly currentUser = computed(() => this._currentUser());

  private readonly user$ = new Observable<SystemUser | null>();

  /**
   * Creates an instance of AuthService.
   * Initializes MSAL account storage events and sets up subscription to authentication status changes.
   *
   * @param msalService - The MSAL service instance for handling authentication
   * @param msalBroadcastService - Service for broadcasting MSAL authentication events
   * @param authClient - The authentication client instance
   */
  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private authClient: AuthClient,
  ) {
    this.isRefreshing.set(true);
    this.user$ = this.refreshToken$.pipe(
      switchMap(() => this.fetchCurrentUser()),
      takeUntilDestroyed(this.destroyRef),
      shareReplay(1),
    );
    this.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((user: SystemUser | null) => {
        this._currentUser.set(user);
        this.isRefreshing.set(false);
      });
    this.updateLoggedInStatus();
  }
  public updateLoggedInStatus(): void {
    this.msalService.instance.enableAccountStorageEvents();
    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.checkAccount());
  }

  get currentUser$(): Observable<SystemUser | null> {
    return this.user$;
  }

  /*
   * Lifecycle hook that cleans up subscriptions when the service is destroyed.
   * Prevents memory leaks by completing the destroying$ Subject.
   */
  b2cUser(): SystemUser {
    const activeAccount = this.msalService.instance.getActiveAccount();
    // Create base user from MSAL account
    const baseUser: SystemUser = {
      externalADId: activeAccount?.idTokenClaims?.sub ?? '',
      firstName: activeAccount?.name?.split(' ')[0] ?? '',
      lastName: activeAccount?.name?.split(' ').slice(1).join(' ') ?? '',
      email: activeAccount?.username ?? '',
      id: -1,
      isAdmin: false,
      roles: [],
      gridSettings: [],
    };
    return baseUser;
  }

  /**
   * Fetches the current user from the API
   * @param activeAccount - The active account
   * @returns The current user
   */
  private fetchCurrentUser(): Observable<SystemUser | null> {
    const b2cUser = this.b2cUser();
    // Merge with API user data
    return this.authClient.getCurrentUser().pipe(
      catchError((error: unknown) => {
        // User authenticated with B2C but has no matching application
        // account (or the API rejected the request). The HTTP interceptor
        // redirects 401s to the error page; here we just emit null so the
        // stream completes cleanly instead of staying in an errored state.
        console.error('Failed to load current user.', error);
        return of(null);
      }),
      switchMap((apiUser: GetAuthenticatedUserQueryResponse | null) => {
        if (apiUser) {
          // Combine MSAL account with API user data
          const combinedUser: SystemUser = {
            ...b2cUser,
            id: apiUser.id ?? null,
            isAdmin: apiUser.currentPharmacy?.role === UserRole.ADMINISTRATOR,
            email: apiUser.email ?? '',
            firstName: apiUser.firstName ?? '',
            lastName: apiUser.lastName ?? '',
            currentPharmacy: this.mapRole(apiUser.currentPharmacy),
            roles: Object.values(apiUser.pharmacyAccessList ?? {}).flatMap(
              (x) => {
                return this.mapRoles(x);
              },
            ),
            gridSettings:
              apiUser.gridSettings?.map((settings) => {
                return {
                  id: settings.id,
                  description: settings.description,
                  key: settings.key,
                  settings: JSON.parse(settings.settings ?? '{}') as GridState,
                  isDefault: settings.isDefault,
                };
              }) ?? [],
            // Environment feature switches; a missing block (older API) means "enabled".
            features: {
              managerAnalytics: apiUser.features?.managerAnalytics ?? true,
              gamification: apiUser.features?.gamification ?? true,
            },
          };
          return of(combinedUser);
        } else {
          // Fall back to just MSAL account data
          return of(b2cUser);
        }
      }),
    );
  }

  private mapRoles(
    pharmacyAccess: PharmacyAccessResponse[] | undefined,
  ): SystemUserAccess[] {
    return pharmacyAccess?.map((x) => this.mapRole(x)) ?? [];
  }

  /**
   * Maps the roles from the API to the SystemUserAccess model
   * @param pharmacyAccess - The roles from the API
   * @returns The mapped roles
   */
  private mapRole(
    pharmacyAccess: PharmacyAccessResponse | undefined,
  ): SystemUserAccess {
    return <SystemUserAccess>{
      pharmacyId: pharmacyAccess?.pharmacyId ?? null,
      pharmacyName: pharmacyAccess?.pharmacyName ?? '',
      roleId: pharmacyAccess?.role ?? null,
      allowAssignOrders: pharmacyAccess?.allowAssignOrders ?? false,
      allowPerformClinicalValidation:
        pharmacyAccess?.allowPerformClinicalValidation ?? false,
      timeZone: pharmacyAccess?.timeZone ?? '',
      pharmacyShortName: pharmacyAccess?.pharmacyShortName ?? '',
      commercializationAccess: pharmacyAccess?.commercializationAccess ?? false,
    };
  }

  /*
   * Checks and manages the active MSAL account.
   * If no active account is set but there are signed-in accounts,
   * sets the first account as the active account.
   * Updates the user state and authentication status accordingly.
   *
   * @private
   */
  private checkAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    let activeAccount = this.msalService.instance.getActiveAccount();
    if (
      !activeAccount &&
      this.msalService.instance.getAllAccounts().length > 0
    ) {
      let accounts = this.msalService.instance.getAllAccounts();
      activeAccount = accounts[0];
      this.msalService.instance.setActiveAccount(activeAccount);
    }
    if (activeAccount) {
      this.refreshToken$.next();
    } else {
      this._currentUser.set(null);
    }
  }

  /**
   * Initiates the logout process using MSAL's redirect flow.
   *
   * `logoutRedirect()` returns a cold Observable, so it must be subscribed
   * to here for the redirect to fire. Callers treat logout as fire-and-forget.
   */
  public logout(): void {
    this.msalService.logoutRedirect().subscribe();
  }

  public refreshCurrentUser() {
    this._currentUser.set(null);
    this.refreshToken$.next();
  }
}
